import React from "react"
import {
  IconBook2,
  IconChevronRight,
  IconDeviceGamepad2,
} from "@tabler/icons-react"
import routes from "@love-irish/routes"

import { GrammarSections } from "./grammar_reference_modal"
import { SectionCard, SectionCards } from "./section_cards"
import { LANGUAGE_SECTIONS, findSection, SectionKey } from "./language_sections"

// URL slug <-> section key. Permalinks read cleanly (/cheatsheet/word-order) while the content is
// keyed by the SectionKey the reference components already use.
const SECTION_SLUGS: Record<SectionKey, string> = {
  SPELLING: "spelling",
  PRONUNCIATION: "pronunciation",
  GRAMMAR: "grammar",
  VSO: "word-order",
  CONJUGATION: "conjugation",
  OGHAM: "ogham",
  CLOSING: "closing",
}

const SLUG_TO_SECTION: Record<string, SectionKey> = Object.fromEntries(
  Object.entries(SECTION_SLUGS).map(([key, slug]) => [slug, key as SectionKey])
)

/** Permalink for a section, via the shared routes helper (no hand-written URLs). */
export const cheatsheetPath = (key: SectionKey) =>
  routes.cheatsheetSectionPath(SECTION_SLUGS[key])

// Curated cross-links to keep learners moving (and, later, help SEO topic-clustering). Kept short and
// hand-picked on purpose – a focused rail, not a resource browser.
const HELPFUL_RESOURCES = [
  {
    href: routes.codexPath(),
    label: "Browse the Codex",
    icon: <IconBook2 size={16} />,
  },
  {
    href: routes.arcadePath(),
    label: "practice in the arcade",
    icon: <IconDeviceGamepad2 size={16} />,
  },
]

// The top-of-page breadcrumb header: "Cheatsheet" (home) and, on a section, the section label.
const PageHeader: React.FC<{ sectionLabel?: string }> = ({ sectionLabel }) => (
  <nav
    aria-label="Breadcrumb"
    className="mb-5 flex items-center gap-1 text-sm text-secondary-500"
  >
    <a
      href={routes.cheatsheetPath()}
      className="font-semibold text-secondary-800 hover:underline"
    >
      Cheatsheet
    </a>
    {sectionLabel && (
      <>
        <IconChevronRight
          size={14}
          className="text-secondary-400"
          aria-hidden
        />
        <span aria-current="page" className="text-secondary-600">
          {sectionLabel}
        </span>
      </>
    )}
  </nav>
)

export interface GrammarReferencePageProps {
  /** URL slug for the section, e.g. "pronunciation" or "word-order". Empty for the overview hub. */
  grammarKey?: string
}

/**
 * Permalinked, standalone frame for the Irish grammar cheat sheet. Two modes:
 * - `/cheatsheet` (no/unknown key): the overview hub – a scannable card grid of every chapter.
 * - `/cheatsheet/:key`: the section, its whole chapter on one page (no pager), with a sidebar that
 *   links the other chapters and a short helpful-resources rail.
 * Same content + card components as the modal – this is just the routed, shareable frame. Client-
 * rendered for now; a server-rendered body (SEO) can slot in behind the same URL later.
 */
export const GrammarReferencePage: React.FC<GrammarReferencePageProps> = ({
  grammarKey,
}) => {
  const sectionKey = SLUG_TO_SECTION[(grammarKey ?? "").toLowerCase()]
  const section = sectionKey ? findSection(sectionKey) : undefined

  return (
    <div className="mx-auto max-w-5xl px-4 pt-10 pb-12">
      <PageHeader sectionLabel={section?.label} />

      {section ? (
        <div className="flex flex-col gap-6 md:flex-row">
          <aside className="md:w-60 md:flex-shrink-0">
            <nav aria-label="Irish grammar chapters">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                Chapters
              </h2>
              <ul className="space-y-1">
                {LANGUAGE_SECTIONS.map((s) => {
                  const active = s.key === sectionKey
                  return (
                    <li key={s.key}>
                      <a
                        href={cheatsheetPath(s.key)}
                        aria-current={active ? "page" : undefined}
                        className={`flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm ${
                          active
                            ? "bg-primary-50 font-semibold text-primary-700"
                            : "text-secondary-700 hover:bg-secondary-50"
                        }`}
                      >
                        <span className="mt-0.5 text-secondary-400">
                          {s.icon}
                        </span>
                        <span>
                          <span className="block">{s.label}</span>
                          <span className="block text-xs font-normal text-secondary-500">
                            {s.description}
                          </span>
                        </span>
                      </a>
                    </li>
                  )
                })}
              </ul>
            </nav>

            <div className="mt-6">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                Helpful resources
              </h3>
              <ul className="space-y-1">
                {HELPFUL_RESOURCES.map((r) => (
                  <li key={r.href}>
                    <a
                      href={r.href}
                      className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-primary-600 hover:bg-secondary-50"
                    >
                      {r.icon}
                      {r.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <main className="min-w-0 flex-1">
            <div className="rounded-xl border border-secondary-200 bg-white p-5 shadow-sm">
              {/* Own the <h1> here (good for a permalinked page); GrammarSections' renderHeader path
                  uses DialogTitle, which only works inside the modal. singlePage renders the whole
                  chapter at once; trackPageViews=false because this is a page, not a modal. */}
              <h1 className="mb-3 text-xl font-bold text-secondary-900">
                {section.entry.title}
              </h1>
              <GrammarSections
                entry={section.entry}
                renderHeader={false}
                singlePage
                trackPageViews={false}
                analyticsName="cheatsheet_page"
              />
            </div>
          </main>
        </div>
      ) : (
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">
            Irish grammar cheat sheet
          </h1>
          <p className="mb-6 mt-1 max-w-2xl text-secondary-600">
            Spelling, pronunciation, gender and mutations, word order, and verb
            conjugation: the essentials, with worked examples.
          </p>
          <SectionCards
            renderCard={(s, number) => (
              <SectionCard
                number={number}
                icon={s.icon}
                label={s.label}
                description={s.description}
                href={cheatsheetPath(s.key)}
              />
            )}
          />
        </div>
      )}
    </div>
  )
}

export default GrammarReferencePage
